<script setup lang="ts">
  import { useAuth } from '@/composition/use-auth';
  import { useOrders } from '@/composition/use-orders';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { ORDER_STATUSES_MAP } from '@/const/orders.const';
  import { OrderStatus, OrderType } from '@/enums/OrderEnums';
  import { PermissionType } from '@/enums/PermissionType';
  import { IOrder } from '@/interfaces/IOrder';
  import { inject, Ref, computed, ref } from 'vue';

  const ui = useUi();
  const modals = useModal();
  const order = inject<Ref<IOrder>>('order')!;
  const ordersHelper = useOrders();
  const auth = useAuth();

  // setStatus requires OrdersManage server-side: an OrdersHistoric-only user
  // (unified history route) would only get error toasts from these buttons.
  const canManageOrders = computed(() =>
    auth.hasPermission(PermissionType.OrdersManage)
  );

  const isCanceledOrDeclined = computed(() => {
    const s = order.value?.status;
    return s === OrderStatus.Canceled || s === OrderStatus.Declined;
  });

  // Archived orders belong to a closed table: their status is frozen (the
  // server rejects changes anyway), so no prev/next/reactivate actions.
  const isArchived = computed(() => !!order.value?.archived);

  const canReactivate = computed(
    () => isCanceledOrDeclined.value && !isArchived.value
  );

  const steps = computed(() => {
    const o = order.value!;

    const steps = [
      {
        label: 'Recibido',
        icon: ORDER_STATUSES_MAP.get(OrderStatus.Received)!.icon,
        statusId: OrderStatus.Received,
        color: ORDER_STATUSES_MAP.get(OrderStatus.Received)!.color,
        dark:
          ORDER_STATUSES_MAP.get(OrderStatus.Received)!.chipTheme === 'dark',
      },
    ];

    switch (o.status) {
      case OrderStatus.Canceled:
      case OrderStatus.Declined:
        steps.push({
          label: o.status === OrderStatus.Canceled ? 'Cancelado' : 'Rechazado',
          icon: ORDER_STATUSES_MAP.get(o.status)!.icon,
          statusId: o.status,
          color: ORDER_STATUSES_MAP.get(o.status)!.color,
          dark: ORDER_STATUSES_MAP.get(o.status)!.chipTheme === 'dark',
        });
        break;
      default:
        if (o.type !== OrderType.Seat) {
          const midStatus =
            o.type === OrderType.Delivery
              ? OrderStatus.Delivering
              : OrderStatus.ReadyToTake;
          steps.push(
            ...[
              {
                label: 'En preparación',
                icon: ORDER_STATUSES_MAP.get(OrderStatus.Progress)!.icon,
                statusId: OrderStatus.Progress,
                color: ORDER_STATUSES_MAP.get(OrderStatus.Progress)!.color,
                dark:
                  ORDER_STATUSES_MAP.get(OrderStatus.Progress)!.chipTheme ===
                  'dark',
              },
              {
                label:
                  o.type === OrderType.Delivery
                    ? 'En camino'
                    : 'Listo para retirar',
                icon: ORDER_STATUSES_MAP.get(midStatus)!.icon,
                statusId: midStatus,
                color: ORDER_STATUSES_MAP.get(midStatus)!.color,
                dark: ORDER_STATUSES_MAP.get(midStatus)!.chipTheme === 'dark',
              },
              {
                label: 'Entregado',
                icon: ORDER_STATUSES_MAP.get(OrderStatus.Finished)!.icon,
                statusId: OrderStatus.Finished,
                color: ORDER_STATUSES_MAP.get(OrderStatus.Finished)!.color,
                dark:
                  ORDER_STATUSES_MAP.get(OrderStatus.Finished)!.chipTheme ===
                  'dark',
              },
            ]
          );
        } else {
          steps.push(
            ...[
              {
                label: 'En preparación',
                icon: ORDER_STATUSES_MAP.get(OrderStatus.Progress)!.icon,
                statusId: OrderStatus.Progress,
                color: ORDER_STATUSES_MAP.get(OrderStatus.Progress)!.color,
                dark:
                  ORDER_STATUSES_MAP.get(OrderStatus.Progress)!.chipTheme ===
                  'dark',
              },
              {
                label: 'Entregado',
                icon: ORDER_STATUSES_MAP.get(OrderStatus.Finished)!.icon,
                statusId: OrderStatus.Finished,
                color: ORDER_STATUSES_MAP.get(OrderStatus.Finished)!.color,
                dark:
                  ORDER_STATUSES_MAP.get(OrderStatus.Finished)!.chipTheme ===
                  'dark',
              },
            ]
          );
        }
    }

    return steps;
  });

  const step = computed(() => {
    switch (order.value?.status) {
      case OrderStatus.Received:
        return 0;
      case OrderStatus.Progress:
      case OrderStatus.Canceled:
      case OrderStatus.Declined:
        return 1;
      case OrderStatus.Delivering:
      case OrderStatus.ReadyToTake:
        return 2;
      case OrderStatus.Finished:
        return order.value?.type === OrderType.Seat ? 2 : 3;
      default:
        return 0;
    }
  });

  const actionButtons = computed(() => {
    const buttons = {
      previousButton: null,
      nextButton: null,
    };

    if (isArchived.value) return buttons;

    if (step.value > 0) {
      const previousStatus = steps.value[step.value - 1];
      if (previousStatus?.statusId !== OrderStatus.Received) {
        buttons.previousButton = {
          label: `${previousStatus.label}`,
          icon: 'mdi-chevron-left',
          color: previousStatus.color,
          statusId: previousStatus.statusId,
          statusIcon: previousStatus.icon,
          dark: previousStatus.dark,
        };
      }
    }

    if (step.value < steps.value.length - 1 && step.value !== 0) {
      const nextStatus = steps.value[step.value + 1];
      buttons.nextButton = {
        label: `${nextStatus.label}`,
        icon: 'mdi-chevron-right',
        color: nextStatus.color,
        statusId: nextStatus.statusId,
        statusIcon: nextStatus.icon,
        dark: nextStatus.dark,
      };
    }

    return buttons;
  });

  const updating = ref(false);
  async function setStatus(status: OrderStatus) {
    if (updating.value) return;

    try {
      updating.value = true;

      const changed = await ordersHelper.setOrderStatus(order.value!, status);
      // Orders outside the pending store set (unified history modal) get no
      // WS/store push: refresh the injected instance so the stepper reflects
      // the change (refreshOrder's Object.assign fallback covers them).
      // Best-effort: the status change already succeeded — a transient
      // refresh failure must not surface as an update error.
      if (changed) {
        try {
          await ordersHelper.refreshOrder({ order: order.value! });
        } catch (refreshError) {
          console.error(
            'Error refreshing order after status change',
            refreshError
          );
        }
      }
    } catch (error) {
      ui.serverError(error, 'Error al actualizar el estado del pedido');
      // Best-effort resync (a network failure here would throw again and
      // escape the handler as an unhandled rejection).
      try {
        await ordersHelper.refreshOrder({ order: order.value! });
      } catch (refreshError) {
        console.error(
          'Error refreshing order after failed update',
          refreshError
        );
      }
    } finally {
      updating.value = false;
    }
  }

  async function reactivateToProgress() {
    if (updating.value) return;

    const o = order.value!;
    const statusText =
      o.status === OrderStatus.Canceled ? 'cancelado' : 'rechazado';

    const confirmed = await modals.confirm({
      title: 'Reactivar pedido',
      message: `Este pedido está ${statusText}. Se reactivará y volverá a estar en preparación.`,
    });

    if (!confirmed) return;

    await setStatus(OrderStatus.Progress);
  }
</script>

<template>
  <div
    class="order-status-container"
    :class="{
      finished: order.status === OrderStatus.Finished,
    }"
  >
    <v-stepper alt-labels :value="step + 1" class="stepper" elevation="0">
      <v-stepper-header>
        <v-stepper-step
          v-for="(s, i) in steps"
          :key="i"
          :step="i + 1"
          :index="i"
          :icon="s.icon"
          :color="s.color"
          :complete-icon="s.icon"
          :complete="step === i"
        >
          {{ s.label }}
        </v-stepper-step>
      </v-stepper-header>
      <div class="stepper-line"></div>
    </v-stepper>
    <div
      v-if="
        canManageOrders &&
        (canReactivate ||
          actionButtons.previousButton ||
          actionButtons.nextButton)
      "
      class="actions"
    >
      <v-btn
        v-if="canReactivate"
        class="action-button reactivate"
        color="green"
        x-small
        dark
        depressed
        block
        :loading="updating"
        @click="reactivateToProgress"
      >
        <v-icon left>mdi-rotate-left</v-icon>
        Reactivar pedido
      </v-btn>

      <template v-else>
        <v-btn
          v-if="actionButtons.previousButton"
          class="action-button previous"
          color="grey"
          x-small
          :dark="actionButtons.previousButton.dark"
          depressed
          outlined
          :loading="updating"
          @click="setStatus(actionButtons.previousButton.statusId)"
        >
          <v-icon left>{{ actionButtons.previousButton.icon }}</v-icon>
          <div class="button-text">
            <span class="button-title">Volver a</span>
            <span class="button-label">{{
              actionButtons.previousButton.label
            }}</span>
          </div>
        </v-btn>
        <div v-else></div>
        <v-btn
          v-if="actionButtons.nextButton"
          class="action-button next"
          :color="actionButtons.nextButton.color"
          x-small
          :dark="actionButtons.nextButton.dark"
          depressed
          :loading="updating"
          @click="setStatus(actionButtons.nextButton.statusId)"
        >
          <v-icon left>{{ actionButtons.nextButton.statusIcon }}</v-icon>
          <div class="button-text">
            <span class="button-title">Marcar como</span>
            <span class="button-label">{{
              actionButtons.nextButton.label
            }}</span>
          </div>
          <v-icon right>{{ actionButtons.nextButton.icon }}</v-icon>
        </v-btn>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .order-status-container {
    @apply tw-border tw-border-gray-300 tw-border-dashed tw-rounded-xl tw-py-2 tw-bg-white tw-space-y-2;

    .stepper {
      @apply tw-relative tw-bg-transparent;

      ::v-deep .v-stepper__step {
        @apply tw-bg-white tw-truncate;
        flex-basis: 95px;
        padding: 5px;
        z-index: 1;

        .v-stepper__label {
          @apply tw-text-center tw-text-sm;
          line-height: 1.2em;
        }
      }

      .stepper-line {
        @apply tw-border-t-2 tw-border-dotted tw-border-gray-300;
        @apply tw-absolute tw-top-4 tw-left-0 tw-right-0;
        z-index: 0;
      }
    }

    &.finished {
      @apply tw-border-green-400 tw-bg-green-50;

      .stepper {
        ::v-deep .v-stepper__step {
          @apply tw-bg-green-50;
        }

        .stepper-line {
          @apply tw-border-green-500;
        }
      }
    }

    .actions {
      @apply tw-flex tw-justify-between tw-space-x-2 tw-px-2;

      .action-button {
        @apply tw-rounded-lg tw-pt-[1rem] tw-pb-[1.1rem];

        .button-text {
          @apply tw-flex tw-flex-col tw-items-start tw-space-y-0.5;

          .button-title {
            @apply tw-text-xs tw-font-normal tw-normal-case;
            line-height: 1em;
          }

          .button-label {
            @apply tw-text-xs tw-font-semibold;
            line-height: 1em;
          }
        }

        &.previous {
        }

        &.next {
        }
      }
    }
  }
</style>
